import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';

import {
  COLOR_SCHEME_PREFERENCE_STORAGE_KEY,
  readStoredColorSchemePreference,
  resolveColorScheme,
  type ColorSchemePreference,
  type ResolvedColorScheme,
} from '@/config/colorScheme';

const DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)';

function subscribeDarkPreference(onStoreChange: () => void): () => void {
  const mq = window.matchMedia(DARK_MEDIA_QUERY);
  mq.addEventListener('change', onStoreChange);
  return () => mq.removeEventListener('change', onStoreChange);
}

function getDarkPreferenceSnapshot(): boolean {
  return window.matchMedia(DARK_MEDIA_QUERY).matches;
}

function getDarkPreferenceServerSnapshot(): boolean {
  return false;
}

export type ColorSchemeContextValue = {
  /** Applied on `document.documentElement` (`data-color-scheme`). */
  resolved: ResolvedColorScheme;
  /** User choice: follow OS or force light/dark. */
  preference: ColorSchemePreference;
  setPreference: (p: ColorSchemePreference) => void;
};

const ColorSchemeContext = createContext<ColorSchemeContextValue | null>(null);

/** Syncs `data-color-scheme` and `color-scheme` on `<html>`. */
export function ColorSchemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ColorSchemePreference>(() =>
    readStoredColorSchemePreference(),
  );

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === COLOR_SCHEME_PREFERENCE_STORAGE_KEY || e.key === null) {
        setPreferenceState(readStoredColorSchemePreference());
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setPreference = useCallback((p: ColorSchemePreference) => {
    setPreferenceState(p);
    try {
      localStorage.setItem(COLOR_SCHEME_PREFERENCE_STORAGE_KEY, p);
    } catch {
      /* ignore */
    }
  }, []);

  const systemIsDark = useSyncExternalStore(
    subscribeDarkPreference,
    getDarkPreferenceSnapshot,
    getDarkPreferenceServerSnapshot,
  );

  const resolved = useMemo(
    () => resolveColorScheme(preference, systemIsDark),
    [preference, systemIsDark],
  );

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-color-scheme', resolved);
    root.style.colorScheme = resolved;
  }, [resolved]);

  const value = useMemo<ColorSchemeContextValue>(
    () => ({ resolved, preference, setPreference }),
    [resolved, preference, setPreference],
  );

  return <ColorSchemeContext.Provider value={value}>{children}</ColorSchemeContext.Provider>;
}

// Hook kèm Provider — HMR vẫn ổn; tách file chỉ để thoả react-refresh là không cần thiết.
// eslint-disable-next-line react-refresh/only-export-components
export function useColorScheme(): ColorSchemeContextValue {
  const ctx = useContext(ColorSchemeContext);
  if (!ctx) {
    throw new Error('useColorScheme must be used within ColorSchemeProvider');
  }
  return ctx;
}
